import { ApolloProvider, useReactiveVar } from "@apollo/client";
import { Outlet, Route, Routes, useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import IdleTimer from "react-idle-timer";
import { OptimizelyProvider } from "@optimizely/react-sdk";
import { useEffect } from "react";

import { IAppState, ILoginData } from "../interfaces/interfaces";
import {
  brilliantUserToken,
  brilliantUserTokenWithLoader,
  logOutUser,
} from "../actions";
import ConsumerPaywall from "../consumer/components/paywall/ConsumerPaywall";
import { oneMinInMs } from "../utils/utils";
import optimizely from "../OptimizelySetUp";

import ErrorsComponent, { NotFoundErrorComponent } from "./errors/Error";
import {
  isAlertSideMenuVisibleVar,
  isSideMenuVisibleVar,
} from "./apollo/LocalState";
import AccountDetails from "./account/AccountDetails";
import AlertPreferencesLandingPage from "./alertManagement/preferences/AlertPreferencesLandingPage";
import AlertSideBar from "./alertManagement/AlertsSideBar";
import Client from "./apollo/Client";
import Configurations from "./configurations/Configurations";
import ConfigurationsDetails from "./configurations/ConfigurationsDetails";
import DeviceDetails from "./deviceDetails/DeviceDetails";
import EditManagers from "./adminManagement/EditManagers";
import EntrataDetailsPage from "./integrations/EntrataDetailsPage";
import EntrataError from "./login/entrata/EntrataError";
import EntrataSuccess from "./login/entrata/EntrataSuccess";
import Footer from "./footer/Footer";
import HeaderHelper from "./header/HeaderHelper";
import HomeDetails from "./homeDetails/HomeDetails";
import Integrations from "./configurations/Integrations";
import InvitedUserRegistration from "./registration/userRegistration/InvitedUserRegistration";
import Login from "./login/Login";
import OrganizationDetails from "./organizations/OrganizationDetails";
import Organizations from "./organizations/Organizations";
import PayMentsAndPayouts from "./organizations/PaymentsAndPayouts";
import PropertyDetails from "./propertyDetails/PropertyDetails";
import RealPageDetailsPage from "./integrations/RealPageDetailsPage";
import RegionDetails from "./propertyDetails/RegionDetails";
import Registration from "./registration/Registration";
import RemoteLock from "./integrations/RemoteLock";
import RemoteLockSuccess from "./integrations/RemoteLockSuccess";
import SideMenu from "./header/SideMenu";
import Spinner from "./Spinner";
import TenantRegistration from "./registration/tenantRegistration/TenantRegistration";
import ToastMessages from "./ToastMessages";
import UnitDetails from "./unitDetails/UnitDetails";
import UserRegistration from "./registration/userRegistration/UserRegistration";
import { setNavigate } from "./navigation";

const Layout: React.FC<{ dispatch: any; isAlertSideMenuVisible: boolean }> = ({
  dispatch,
  isAlertSideMenuVisible,
}) => (
  <>
    <ToastMessages />
    <IdleTimer
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onIdle={() => dispatch(logOutUser())}
      timeout={oneMinInMs * 30}
    />
    <HeaderHelper />
    <div className="app-body app-body-side-bar">
      <div className={`${isAlertSideMenuVisible && "app-body-hidden"}`}>
        <Outlet />
      </div>
      {isAlertSideMenuVisible && <AlertSideBar />}
    </div>
    <Footer />
  </>
);

const App: React.FC<{
  user: ILoginData;
  loading: boolean;
}> = (props) => {
  const { user, loading } = props;
  const dispatch = useDispatch();

  const isSideMenuVisible = useReactiveVar(isSideMenuVisibleVar);
  const isAlertSideMenuVisible = useReactiveVar(isAlertSideMenuVisibleVar);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(brilliantUserTokenWithLoader());
    setInterval(() => {
      dispatch(brilliantUserToken());
    }, oneMinInMs * 5);
  }, [dispatch, oneMinInMs]);

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  // Will replace setAttribute with actual color from backend.
  // const root = document.querySelector(":root") as Element;
  // root.setAttribute("style", "--app-color: red");

  return (
    <OptimizelyProvider
      optimizely={optimizely}
      user={{
        attributes: {
          customerEmail: user.email,
        },
        id: user.userId,
      }}
    >
      <ApolloProvider client={Client}>
        <div className="main-container">
          {loading && <Spinner />}
          {isSideMenuVisible && <SideMenu />}
          <Routes>
            <Route path="/entrata/link" element={<Login />} />
            <Route path="/register" element={<Registration />} />
            <Route path="/register-tenant" element={<TenantRegistration />} />
            <Route path="/create-user-account" element={<UserRegistration />} />
            <Route
              path="/create-invited-user-account"
              element={<InvitedUserRegistration />}
            />
            <Route path="/entrata/success" element={<EntrataSuccess />} />
            <Route path="/entrata/error" element={<EntrataError />} />
            <Route path="/remotelock/success" element={<RemoteLockSuccess />} />
            <Route element={<ConsumerPaywall />} path="/consumer/paywall" />

            {user.isAuthenticated ? (
              <Route
                element={
                  <Layout
                    dispatch={dispatch}
                    isAlertSideMenuVisible={isAlertSideMenuVisible}
                  />
                }
              >
                <Route path="/" element={<Organizations />} />
                <Route
                  path="/:organizationId/organization"
                  element={<OrganizationDetails />}
                />
                <Route
                  path="/:organizationId/configurations"
                  element={<Configurations />}
                />
                <Route
                  path="/:organizationId/integrations"
                  element={<Integrations />}
                />
                <Route
                  path="/:organizationId/configurations/:configurationId"
                  element={<ConfigurationsDetails />}
                />
                <Route
                  path="/:organizationId/entrata"
                  element={<EntrataDetailsPage />}
                />
                <Route
                  path="/:organizationId/alertPreferences"
                  element={<AlertPreferencesLandingPage />}
                >
                  <Route
                    path="/:organizationId/alertPreferences/:propertyId"
                    element={<AlertPreferencesLandingPage />}
                  />
                </Route>
                <Route
                  path="/:organizationId/realPage"
                  element={<RealPageDetailsPage />}
                />
                <Route
                  path="/:organizationId/payments-payouts"
                  element={<PayMentsAndPayouts />}
                />
                <Route
                  path="/:organizationId/remoteLock"
                  element={<RemoteLock />}
                >
                  <Route
                    path="/:organizationId/remoteLock/:buildingId"
                    element={<RemoteLock />}
                  />
                </Route>
                <Route
                  path="/:organizationId/buildings/:buildingId"
                  element={<PropertyDetails />}
                />
                <Route
                  path="/:organizationId/homes/:regionId"
                  element={<RegionDetails />}
                />
                <Route
                  path="/:organizationId/units/:propertyId"
                  element={<UnitDetails />}
                />
                <Route
                  path="/:organizationId/home/:propertyId"
                  element={<HomeDetails />}
                />
                <Route
                  path="/:organizationId/home/:propertyId/device/:deviceId"
                  element={<DeviceDetails />}
                />
                <Route
                  path="/:organizationId/managers"
                  element={<EditManagers />}
                />
                <Route path="/accountDetails" element={<AccountDetails />} />
                <Route path="/errors/:status" element={<ErrorsComponent />} />
                <Route path="*" element={<NotFoundErrorComponent />} />
              </Route>
            ) : (
              <Route path="*" element={<Login />} />
            )}
          </Routes>
        </div>
      </ApolloProvider>
    </OptimizelyProvider>
  );
};

const mapStateToProps = (state: IAppState) => {
  return {
    loading: state.loading,
    user: state.user,
  };
};

export default connect(mapStateToProps)(App);
